import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import { API_BASE_URL } from "../config/constants";

const FP_REQUEST_ENDPOINT = `${API_BASE_URL}/auth/forgot/request`;
const FP_VERIFY_ENDPOINT = `${API_BASE_URL}/auth/forgot/verify`;

const MAX_ATTEMPTS = 10;

const ForgotPasswordScreen = () => {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [otpStage, setOtpStage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSecsLeft, setResendSecsLeft] = useState(0);
  const [attemptsUsed, setAttemptsUsed] = useState(0);
  const [attemptsRemaining, setAttemptsRemaining] = useState(MAX_ATTEMPTS);
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  useEffect(() => {
    if (resendSecsLeft <= 0) return;
    const id = setInterval(
      () => setResendSecsLeft((s) => (s > 0 ? s - 1 : 0)),
      1000
    );
    return () => clearInterval(id);
  }, [resendSecsLeft]);

  const canResend = resendSecsLeft <= 0 && attemptsRemaining > 0 && !isResending;

  const requestOtp = async () => {
    if (!mobile || mobile.length < 10) {
      toast.error("Enter valid 10-digit mobile number");
      return;
    }
    if (resendSecsLeft > 0) return;

    setLoading(true);
    try {
      const resp = await fetch(FP_REQUEST_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile_number: mobile }),
      });
      const json = await resp.json().catch(() => ({}));

      if (resp.ok && json?.success) {
        setOtpStage(true);
        const used = Number(json?.attempts_used ?? attemptsUsed + 1);
        const remain = Number(
          json?.attempts_remaining ?? Math.max(0, MAX_ATTEMPTS - used)
        );
        const cooldown = Number(json?.cooldown_seconds ?? 120);

        setAttemptsUsed(used);
        setAttemptsRemaining(remain);
        setResendSecsLeft(cooldown);
        toast.success(`OTP sent. Attempts left: ${remain}`);
      } else {
        const retry = Number(json?.retry_after_seconds ?? 0);
        if (retry > 0) setResendSecsLeft(retry);
        toast.error(json?.message || "Unable to send OTP");
      }
    } catch (e) {
      toast.error(e?.message || "Network error");
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    if (attemptsRemaining <= 0) {
      toast.error("Daily OTP limit reached");
      return;
    }
    if (resendSecsLeft > 0) return;

    setIsResending(true);
    try {
      const resp = await fetch(FP_REQUEST_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile_number: mobile }),
      });
      const json = await resp.json().catch(() => ({}));

      if (resp.ok && json?.success) {
        const used = Number(json?.attempts_used ?? attemptsUsed + 1);
        const remain = Number(
          json?.attempts_remaining ?? Math.max(0, MAX_ATTEMPTS - used)
        );
        const cooldown = Number(json?.cooldown_seconds ?? 120);
        setAttemptsUsed(used);
        setAttemptsRemaining(remain);
        setResendSecsLeft(cooldown);
        toast.success(`OTP resent. Attempts left: ${remain}`);
      } else {
        const retry = Number(json?.retry_after_seconds ?? 0);
        if (retry > 0) setResendSecsLeft(retry);
        toast.error(json?.message || "Unable to resend OTP");
      }
    } catch (e) {
      toast.error(e?.message || "Network error");
    } finally {
      setIsResending(false);
    }
  };

  const verifyAndReset = async () => {
    if (!otp || otp.length !== 4) {
      toast.error("Enter 4-digit OTP");
      return;
    }
    if (!newPass || newPass.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (newPass !== confirmPass) {
      toast.error("Passwords do not match");
      return;
    }

    setVerifying(true);
    try {
      const resp = await fetch(FP_VERIFY_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobile_number: mobile,
          otp: otp,
          new_password: newPass,
        }),
      });
      const json = await resp.json().catch(() => ({}));

      if (resp.ok && json?.success) {
        toast.success("Password reset successful");
        navigate(-1);
      } else {
        toast.error(json?.message || "Reset failed");
      }
    } catch (e) {
      toast.error(e?.message || "Network error");
    } finally {
      setVerifying(false);
    }
  };

  const resendLabel = useMemo(() => {
    if (attemptsRemaining <= 0) return "Daily limit reached";
    if (resendSecsLeft > 0) {
      const mm = String(Math.floor(resendSecsLeft / 60)).padStart(2, "0");
      const ss = String(resendSecsLeft % 60).padStart(2, "0");
      return `Resend OTP in ${mm}:${ss}`;
    }
    return "Resend OTP";
  }, [attemptsRemaining, resendSecsLeft]);

  return (
    <Page>
      <Card>
        <Title>Reset Password</Title>
        <Sub>Enter your registered mobile number to receive OTP.</Sub>

        <FieldRow>
          <input
            value={mobile}
            onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
            placeholder="Enter mobile number"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={10}
            disabled={otpStage}
          />
        </FieldRow>

        {!otpStage ? (
          <PrimaryBtn onClick={requestOtp} disabled={loading}>
            {loading ? "Sending..." : "Send OTP"}
          </PrimaryBtn>
        ) : (
          <>
            <FieldRow>
              <input
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="Enter 4-digit OTP"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
              />
            </FieldRow>
            <FieldRow>
              <input
                type={showNewPass ? "text" : "password"}
                value={newPass}
                onChange={(e) => setNewPass(e.target.value)}
                placeholder="New password"
              />
              <ToggleBtn type="button" onClick={() => setShowNewPass((v) => !v)}>
                {showNewPass ? "Hide" : "Show"}
              </ToggleBtn>
            </FieldRow>
            <FieldRow>
              <input
                type={showConfirmPass ? "text" : "password"}
                value={confirmPass}
                onChange={(e) => setConfirmPass(e.target.value)}
                placeholder="Confirm password"
              />
              <ToggleBtn
                type="button"
                onClick={() => setShowConfirmPass((v) => !v)}
              >
                {showConfirmPass ? "Hide" : "Show"}
              </ToggleBtn>
            </FieldRow>

            <GhostBtn onClick={resendOtp} disabled={!canResend}>
              {isResending ? "Resending..." : resendLabel}
            </GhostBtn>

            <PrimaryBtn onClick={verifyAndReset} disabled={verifying}>
              {verifying ? "Resetting..." : "Reset Password"}
            </PrimaryBtn>
          </>
        )}

        <BackLink onClick={() => navigate(-1)}>Back to login</BackLink>
      </Card>
    </Page>
  );
};

export default ForgotPasswordScreen;

const Page = styled.div`
  min-height: 100vh;
  display: grid;
  place-items: center;
  background: #f1f5f9;
  padding: 20px;
`;

const Card = styled.div`
  width: min(520px, 92vw);
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
  display: grid;
  gap: 12px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: #111827;
  text-align: center;
`;

const Sub = styled.p`
  margin: 0;
  font-size: 13px;
  color: #6b7280;
  text-align: center;
`;

const FieldRow = styled.div`
  position: relative;

  input {
    width: 100%;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    background: #f8fafc;
    padding: 12px 44px 12px 12px;
    font-size: 14px;
    box-sizing: border-box;
  }
`;

const ToggleBtn = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: var(--app-primary);
  font-weight: 700;
  cursor: pointer;
`;

const PrimaryBtn = styled.button`
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  background: var(--app-primary);
  color: #fff;
  font-weight: 700;
  border: none;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
`;

const GhostBtn = styled.button`
  width: 100%;
  padding: 10px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px dashed #cbd5f5;
  color: #0f172a;
  font-weight: 700;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
`;

const BackLink = styled.button`
  border: none;
  background: transparent;
  color: var(--app-primary);
  font-weight: 700;
  cursor: pointer;
`;
